import { Badge } from "@/components/ui/badge";

const STATUS_CONFIG: Record<string, { label: string; variant: string; className: string }> = {
  not_yet_reached: { label: "Not Yet Reached", variant: "outline", className: "border-gray-300 text-gray-600" },
  queued: { label: "Queued", variant: "outline", className: "border-amber-400 text-amber-700 bg-amber-50" },
  targeted: { label: "Targeted", variant: "outline", className: "border-blue-400 text-blue-700 bg-blue-50" },
  nurture_monthly: { label: "Nurture Monthly", variant: "outline", className: "border-purple-400 text-purple-700 bg-purple-50" },
  nurture_3month: { label: "Nurture 3mo", variant: "outline", className: "border-purple-300 text-purple-600 bg-purple-50" },
  nurture_6month: { label: "Nurture 6mo", variant: "outline", className: "border-purple-200 text-purple-500 bg-purple-50" },
  disqualified: { label: "Disqualified", variant: "outline", className: "border-red-300 text-red-600 bg-red-50" },
  parked: { label: "Parked", variant: "outline", className: "border-gray-400 text-gray-600 bg-gray-50" },
};

const PRIORITY_CONFIG: Record<string, { label: string; className: string }> = {
  high: { label: "High", className: "border-red-400 text-red-700 bg-red-50" },
  medium: { label: "Medium", className: "border-amber-400 text-amber-700 bg-amber-50" },
  low: { label: "Low", className: "border-gray-300 text-gray-600 bg-gray-50" },
};

export function TargetingStatusBadge({ status }: { status: string | null }) {
  const key = status || "not_yet_reached";
  const config = STATUS_CONFIG[key] || STATUS_CONFIG.not_yet_reached;
  return (
    <Badge variant="outline" className={`text-xs font-medium ${config.className}`}>
      {config.label}
    </Badge>
  );
}

export function PriorityBadge({ priority }: { priority: string | null }) {
  if (!priority) return <span className="text-muted-foreground text-sm">—</span>;
  const config = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.low;
  return (
    <Badge variant="outline" className={`text-xs font-medium ${config.className}`}>
      {config.label}
    </Badge>
  );
}

// Account Operating Model badges

const ACCOUNT_TIER_CONFIG: Record<string, { label: string; className: string }> = {
  // 'unreviewed' was migrated into 'new' on 2026-05-21 (migration collapse_unreviewed_into_new_2026_05_21).
  // Alias kept for any cached client data; should never appear on live rows.
  unreviewed: { label: "New (Unreviewed)", className: "border-gray-300 text-gray-500 bg-gray-50" },
  new: { label: "New (Unreviewed)", className: "border-gray-300 text-gray-500 bg-gray-50" },
  cold_target: { label: "Cold Target", className: "border-blue-300 text-blue-600 bg-blue-50" },
  warm_target: { label: "Warm Target", className: "border-amber-400 text-amber-700 bg-amber-50" },
  active_prospect: { label: "Active Prospect", className: "border-green-400 text-green-700 bg-green-50" },
  customer: { label: "Customer", className: "border-emerald-400 text-emerald-700 bg-emerald-50" },
  disqualified: { label: "Disqualified", className: "border-red-300 text-red-600 bg-red-50" },
};

export function AccountTierBadge({ status }: { status: string | null }) {
  const key = status || "new";
  const config = ACCOUNT_TIER_CONFIG[key] || ACCOUNT_TIER_CONFIG.new;
  return (
    <Badge variant="outline" className={`text-xs font-medium ${config.className}`}>
      {config.label}
    </Badge>
  );
}

const ENRICHMENT_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  not_started: { label: "Not Started", className: "border-gray-300 text-gray-500 bg-gray-50" },
  pending: { label: "Pending", className: "border-amber-300 text-amber-600 bg-amber-50" },
  partial: { label: "Partial", className: "border-yellow-400 text-yellow-700 bg-yellow-50" },
  enriched: { label: "Enriched", className: "border-blue-400 text-blue-700 bg-blue-50" },
  complete: { label: "Complete", className: "border-green-400 text-green-700 bg-green-50" },
  dead_end: { label: "Dead End", className: "border-stone-400 text-stone-600 bg-stone-100" },
  needs_manual: { label: "Needs Manual", className: "border-orange-400 text-orange-700 bg-orange-50" },
};

export function EnrichmentStatusBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-muted-foreground text-sm">—</span>;
  const config = ENRICHMENT_STATUS_CONFIG[status] || ENRICHMENT_STATUS_CONFIG.not_started;
  return (
    <Badge variant="outline" className={`text-xs font-medium ${config.className}`}>
      {config.label}
    </Badge>
  );
}

// Backward-compat alias — remove after Lovable push confirms no references
export const AccountStatusBadge = AccountTierBadge;

export { STATUS_CONFIG, PRIORITY_CONFIG, ACCOUNT_TIER_CONFIG, ENRICHMENT_STATUS_CONFIG };
